import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid2 as Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  AVAILABLE_STATUS_ACTIONS,
  RESERVATION_STATUS_COLOR,
  RESERVATION_STATUS_LABEL,
  ReservationDetailView,
  ReservationListItem,
  STATUS_ACTION_LABEL,
  TimelineView,
  formatReservationDateTime,
} from '../../types/reservation';

/**
 * 予約が 1 件も無い店舗向けの「サンプル予約を表示」機能（フィードバック1・タスク16）。
 * 予約が入ったときの一覧・台帳・詳細の見え方を FE ローカルのモックデータだけで確認できるようにする。
 * API は一切呼ばず、操作ボタンはすべて無効化して「サンプル」バッジを付ける。
 */

/** サンプルの素材（時刻・席は選択中の日付に合わせて組み立てる） */
const SAMPLE_SOURCES = [
  {
    id: 'sample-1',
    reservationNumber: 'SAMPLE-001',
    userName: '山田 太郎',
    startTime: '18:00',
    endTime: '20:00',
    numberOfPeople: 4,
    status: 'CONFIRMED' as const,
    selectedMenuName: '季節のおまかせコース',
    selectedMenuPrice: 6000,
    userEmail: 'sample-taro@example.com',
    userPhone: '090-0000-0000',
    seatTypeId: 'sample-seat-table',
    seatTypeName: 'テーブル席',
    responses: [
      { question: 'アレルギーはありますか？', inquiryType: 'TEXT' as const, answer: 'えび・かに' },
      { question: 'ご利用目的', inquiryType: 'CHOICE' as const, answer: '記念日' },
    ],
    staffNote: '窓際の席をご希望。デザートプレートを用意する。',
  },
  {
    id: 'sample-2',
    reservationNumber: 'SAMPLE-002',
    userName: '佐藤 花子',
    startTime: '18:30',
    endTime: '20:30',
    numberOfPeople: 2,
    status: 'REQUESTED' as const,
    selectedMenuName: null,
    selectedMenuPrice: null,
    userEmail: 'sample-hanako@example.com',
    userPhone: null,
    seatTypeId: 'sample-seat-counter',
    seatTypeName: 'カウンター',
    responses: [],
    staffNote: null,
  },
  {
    id: 'sample-3',
    reservationNumber: 'SAMPLE-003',
    userName: '鈴木 一郎',
    startTime: '19:00',
    endTime: '21:30',
    numberOfPeople: 6,
    status: 'CONFIRMED' as const,
    selectedMenuName: '飲み放題付き宴会コース',
    selectedMenuPrice: 5500,
    userEmail: null,
    userPhone: '080-0000-0000',
    seatTypeId: 'sample-seat-room',
    seatTypeName: '個室',
    responses: [
      { question: 'アレルギーはありますか？', inquiryType: 'TEXT' as const, answer: 'なし' },
    ],
    staffNote: null,
  },
];

/** サンプル一覧（選択中の日付の日時で組み立てる） */
export const buildSampleList = (date: string): ReservationListItem[] =>
  SAMPLE_SOURCES.map((s) => ({
    id: s.id,
    reservationNumber: s.reservationNumber,
    userName: s.userName,
    reservationTime: `${date}T${s.startTime}:00`,
    numberOfPeople: s.numberOfPeople,
    status: s.status,
    selectedMenuName: s.selectedMenuName,
    seatTypeName: s.seatTypeName,
    units: 1,
  }));

/** サンプル台帳（席タイプ別レーンの見え方も確認できるよう席タイプ付きで返す） */
export const buildSampleTimeline = (date: string): TimelineView => ({
  date,
  capacityMode: 'SEAT_TYPE',
  hours: [{ open: '17:00', close: '22:00' }],
  seatTypes: [
    {
      id: 'sample-seat-table',
      name: 'テーブル席',
      capacityMin: 2,
      capacityMax: 4,
      unitCount: 3,
      sortIndex: 0,
    },
    {
      id: 'sample-seat-counter',
      name: 'カウンター',
      capacityMin: 1,
      capacityMax: 2,
      unitCount: 4,
      sortIndex: 1,
    },
    {
      id: 'sample-seat-room',
      name: '個室',
      capacityMin: 4,
      capacityMax: 6,
      unitCount: 1,
      sortIndex: 2,
    },
  ],
  entries: SAMPLE_SOURCES.map((s) => ({
    reservationId: s.id,
    reservationNumber: s.reservationNumber,
    guestName: s.userName,
    partySize: s.numberOfPeople,
    status: s.status,
    start: `${date}T${s.startTime}:00`,
    end: `${date}T${s.endTime}:00`,
    seatTypeId: s.seatTypeId,
    assignedBy: 'AUTO',
  })),
});

/** サンプル詳細ビュー */
export const buildSampleDetail = (id: string, date: string): ReservationDetailView | null => {
  const s = SAMPLE_SOURCES.find((src) => src.id === id);
  if (!s) return null;
  return {
    id: s.id,
    reservationNumber: s.reservationNumber,
    userName: s.userName,
    userEmail: s.userEmail,
    userPhone: s.userPhone,
    reservationTime: `${date}T${s.startTime}:00`,
    numberOfPeople: s.numberOfPeople,
    status: s.status,
    cancellationReason: null,
    selectedMenuName: s.selectedMenuName,
    selectedMenuPrice: s.selectedMenuPrice,
    responses: s.responses,
    payment: null,
    stayMinutes: 120,
    endTime: `${date}T${s.endTime}:00`,
    seatAssignment: {
      seatTypeId: s.seatTypeId,
      seatTypeName: s.seatTypeName,
      assignedBy: 'AUTO',
    },
    staffNote: s.staffNote,
  };
};

/** 「サンプル」バッジ（一覧行・詳細ダイアログで共用） */
export const SampleChip: React.FC = () => (
  <Chip size="small" label="サンプル" color="secondary" variant="outlined" />
);

interface SampleDetailDialogProps {
  detail: ReservationDetailView | null;
  onClose: () => void;
}

/**
 * サンプル予約の詳細レイアウト確認用ダイアログ。
 * 実際の予約詳細画面（ReservationDetail）と同じ構成のセクションを、操作ボタンを無効化して表示する。
 */
export const SampleReservationDetailDialog: React.FC<SampleDetailDialogProps> = ({
  detail,
  onClose,
}) => {
  const infoRow = (label: string, value: React.ReactNode) => (
    <Grid container spacing={1} sx={{ py: 0.5 }}>
      <Grid size={{ xs: 4, sm: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Grid>
      <Grid size={{ xs: 8, sm: 9 }}>
        <Typography variant="body2" component="div">
          {value}
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <Dialog open={detail !== null} onClose={onClose} fullWidth maxWidth="md">
      <DialogContent>
        {detail && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              サンプル予約の詳細イメージです。実際の予約では各操作が行えます。
            </Typography>

            {/* 基本情報（実画面と同じレイアウト） */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6">{detail.userName} 様</Typography>
                <Chip
                  label={RESERVATION_STATUS_LABEL[detail.status]}
                  color={RESERVATION_STATUS_COLOR[detail.status]}
                  size="small"
                />
                <SampleChip />
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {detail.reservationNumber}
                </Typography>
              </Box>
              {infoRow('日時', formatReservationDateTime(detail.reservationTime))}
              {detail.stayMinutes != null &&
                infoRow(
                  '滞在時間',
                  `${detail.stayMinutes}分${detail.endTime ? `（〜${detail.endTime.substring(11, 16)}）` : ''}`,
                )}
              {infoRow('人数', `${detail.numberOfPeople}名`)}
              {infoRow(
                'コース',
                detail.selectedMenuName
                  ? `${detail.selectedMenuName}${detail.selectedMenuPrice != null ? `（${detail.selectedMenuPrice.toLocaleString()}円）` : ''}`
                  : 'なし（席のみ）',
              )}
              {infoRow('メール', detail.userEmail ?? '-')}
              {infoRow('電話', detail.userPhone ?? '-')}
              {detail.responses.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    質問への回答
                  </Typography>
                  {detail.responses.map((r, i) => (
                    <React.Fragment key={i}>{infoRow(r.question, r.answer || '-')}</React.Fragment>
                  ))}
                </>
              )}

              {/* ステータス操作（サンプルのため無効化して見た目だけ再現する） */}
              {AVAILABLE_STATUS_ACTIONS[detail.status].length > 0 && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {AVAILABLE_STATUS_ACTIONS[detail.status].map((action) => (
                      <Button
                        key={action}
                        variant={
                          action === 'CONFIRM' || action === 'COMPLETE' ? 'contained' : 'outlined'
                        }
                        disabled
                      >
                        {STATUS_ACTION_LABEL[action]}
                      </Button>
                    ))}
                  </Box>
                </>
              )}
            </Paper>

            {/* 席割当 */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                席割当
              </Typography>
              {detail.seatAssignment ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={detail.seatAssignment.seatTypeName}
                    color="primary"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    自動割当
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  未割当
                </Typography>
              )}
              <Button size="small" variant="outlined" sx={{ mt: 1 }} disabled>
                席割当を変更
              </Button>
            </Paper>

            {/* スタッフメモ */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                スタッフメモ（お客様には表示されません）
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={detail.staffNote ?? ''}
                placeholder="アレルギー・席の希望・引き継ぎ事項など"
                disabled
              />
              <Button size="small" variant="outlined" sx={{ mt: 1 }} disabled>
                メモを保存
              </Button>
            </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          閉じる
        </Button>
      </DialogActions>
    </Dialog>
  );
};
